import { SearchStatus } from '../../+types/search-status.type';
import { PdfViewerEventType, PdfViewerNavigateToPageNumberEvent, PdfViewerInitializeSearchEvent, PdfViewerGoToNextSearchResultPageEvent, PdfViewerGoToPreviousSearchResultPageEvent } from '../../+types/pdf-viewer-event.type';
import { PdfViewerTranslationKey } from '../../+types/pdf-viewer-translations.type';
import { PDF_VIEWER_SVG_ICONS, PdfViewerIconType } from './../../+types/pdf-viewer-icon.type';
import { KeyCodeName } from '../../../../types/key-code-name.type';
import { debounce } from 'lodash-es';
import { PdfViewerHideSearchPendoTrackEvent, PdfViewerGoToNextSearchResultPagePendoTrackEvent, PdfViewerGoToPreviousSearchResultPagePendoTrackEvent, PdfViewerShowSearchPendoTrackEvent } from '../../+types/pdf-viewer-pendo-track-event.type';
export class PdfSearchComponent {
    constructor(currentPageNumber, container, eventBus, searchService, translations, pendoService) {
        this.currentPageNumber = currentPageNumber;
        this.container = container;
        this.eventBus = eventBus;
        this.searchService = searchService;
        this.translations = translations;
        this.pendoService = pendoService;
        this.isSearchAvailable = false;
        this.goToPrevPage = () => {
            const prevPageNumber = this.prevPageNumber;
            if (this.currentPageNumber === prevPageNumber) {
                return;
            }
            this.eventBus.dispatch(new PdfViewerGoToPreviousSearchResultPageEvent());
            this.goToPage(prevPageNumber);
        };
        this.goToNextPage = () => {
            const nextPageNumber = this.nextPageNumber;
            if (this.currentPageNumber === nextPageNumber) {
                return;
            }
            this.eventBus.dispatch(new PdfViewerGoToNextSearchResultPageEvent());
            this.goToPage(nextPageNumber);
        };
        this.goToPage = (pageNumber) => {
            this.eventBus.dispatch(new PdfViewerNavigateToPageNumberEvent({ pageNumber }));
        };
        this.hideSearch = () => {
            var _a;
            (_a = this.pendoService) === null || _a === void 0 ? void 0 : _a.track(new PdfViewerHideSearchPendoTrackEvent());
            this.container.innerHTML = '';
            this.initializeSearch('', true);
        };
        this.onPdfViewerSearchIsAvailableEvent = () => {
            this.isSearchAvailable = true;
        };
        this.onGoToPrevPageClick = () => {
            var _a;
            (_a = this.pendoService) === null || _a === void 0 ? void 0 : _a.track(new PdfViewerGoToPreviousSearchResultPagePendoTrackEvent({ trigger: 'click' }));
            this.goToPrevPage();
        };
        this.onGoToNextPageClick = () => {
            var _a;
            (_a = this.pendoService) === null || _a === void 0 ? void 0 : _a.track(new PdfViewerGoToNextSearchResultPagePendoTrackEvent({ trigger: 'click' }));
            this.goToNextPage();
        };
        this.onInputKeydownEvent = (event) => {
            var _a, _b;
            // Prevent navigation when using arrows
            if (event.key === KeyCodeName.ArrowLeft || event.key === KeyCodeName.ArrowRight) {
                event.stopPropagation();
            }
            if (this.searchState.status === SearchStatus.Pending) {
                return;
            }
            if (this.searchState.status === SearchStatus.Done && this.searchState.pages.length === 0) {
                return;
            }
            if (event.key === KeyCodeName.Enter) {
                if (event.shiftKey) {
                    this.goToPrevPage();
                    (_a = this.pendoService) === null || _a === void 0 ? void 0 : _a.track(new PdfViewerGoToPreviousSearchResultPagePendoTrackEvent({ trigger: 'keyboard' }));
                }
                else {
                    this.goToNextPage();
                    (_b = this.pendoService) === null || _b === void 0 ? void 0 : _b.track(new PdfViewerGoToNextSearchResultPagePendoTrackEvent({ trigger: 'keyboard' }));
                }
            }
        };
        this.onInputInputEvent = (event) => {
            this.initializeSearch(this.input.value, false);
            this.initializeSearchDebounced();
        };
        this.onInputFocusinEvent = () => {
            this.searchElement.classList.add('focus');
        };
        this.onInputFocusoutEvent = () => {
            this.searchElement.classList.remove('focus');
        };
        this.onWindowKeydownEvent = (event) => {
            var _a;
            if (event.key === 'f' && (event.metaKey || event.ctrlKey)) {
                event.preventDefault();
                if (this.isSearchAvailable && !this.isDisplayed()) {
                    (_a = this.pendoService) === null || _a === void 0 ? void 0 : _a.track(new PdfViewerShowSearchPendoTrackEvent({ trigger: 'keyboard' }));
                    this.showSearch();
                }
            }
        };
        this.onPdfViewerToggleSearch = () => {
            var _a;
            if (this.isDisplayed()) {
                this.hideSearch();
            }
            else {
                this.showSearch();
                (_a = this.pendoService) === null || _a === void 0 ? void 0 : _a.track(new PdfViewerShowSearchPendoTrackEvent({ trigger: 'click' }));
            }
        };
        this.onPdfViewerHideSearch = () => {
            this.hideSearch();
        };
        this.onPdfViewerFocusChangeEvent = (event) => {
            this.currentPageNumber = event.payload.pdfViewerPage.pageNumber;
        };
        this.onPdfViewerInitializeSearchEvent = () => {
            this.setSearchState(this.searchState);
        };
        this.onPdfViewerFetchSearchResultsEvent = () => {
            this.setSearchState(this.searchState);
        };
        this.initializeSearch = (text, isDebounced = true) => {
            this.eventBus.dispatch(new PdfViewerInitializeSearchEvent({ text: text, isDebounced }));
        };
        this.eventBus.on(PdfViewerEventType.ToggleSearch, this.onPdfViewerToggleSearch);
        this.eventBus.on(PdfViewerEventType.SearchIsAvailable, this.onPdfViewerSearchIsAvailableEvent);
        window.addEventListener('keydown', this.onWindowKeydownEvent);
    }
    get searchState() {
        return this.searchService.searchState;
    }
    get prevPageNumber() {
        const pages = this.searchState.pages;
        return pages[this.searchState.pages.indexOf(this.currentPageNumber) - 1] || pages[pages.length - 1];
    }
    get nextPageNumber() {
        const pages = this.searchState.pages;
        return pages[pages.indexOf(this.currentPageNumber) + 1] || pages[0];
    }
    render() {
        this.container.innerHTML = `
        <div data-id="search" class="sp-pdf-search">
            <div class="magnifying-glass">
                ${PDF_VIEWER_SVG_ICONS[PdfViewerIconType.SearchOutlined]}
            </div>

            <div class="input-wrapper">
                <input data-test-label="sp-pdf-viewer-search-input" type="text" placeholder="${this.translations[PdfViewerTranslationKey.SearchComponentSearchDocument]}" />
            </div>

            <div data-id="loading" class="sp-av-ds-spinner loading">
                <div class="sp-av-ds-spinner_buffer" style="border-width: 2px;"></div>
                <div class="sp-av-ds-spinner_container" role="progressbar">
                    <div class="sp-av-ds-spinner-layer">
                        <div class="sp-av-ds-spinner-circle-clipper sp-av-ds-spinner-circle-clipper--left">
                            <div class="sp-av-ds-spinner-circle-clipper__stroke" style="border-width: 2px;"></div>
                        </div>
                        <div class="sp-av-ds-spinner-circle-clipper sp-av-ds-spinner-circle-clipper--right">
                            <div class="sp-av-ds-spinner-circle-clipper__stroke" style="border-width: 2px;"></div>
                        </div>
                    </div>
                </div>
            </div>
            <div data-id="noResults" class="no-results">
                ${this.translations[PdfViewerTranslationKey.SearchComponentNoResults]}
            </div>
            <button data-test-label="sp-pdf-viewer-search-prev-button" data-id="prevButton" class="sp-pdf-search-button sp-pdf-search-button--no-border-radius sp-pdf-search-button--icon-16">
                <span class="sp-pdf-search-button_wrapper">
                    <span class="icon">
                        ${PDF_VIEWER_SVG_ICONS[PdfViewerIconType.UpFilled]}
                    </span>
                </span>
                <span class="sp-pdf-search-button_focus-overlay"></span>
            <button data-test-label="sp-pdf-viewer-search-next-button" data-id="nextButton" class="sp-pdf-search-button sp-pdf-search-button--no-border-radius sp-pdf-search-button--icon-16">
                <span class="sp-pdf-search-button_wrapper">
                    <span class="icon">
                        ${PDF_VIEWER_SVG_ICONS[PdfViewerIconType.DownFilled]}
                    </span>
                </span>
                <span class="sp-pdf-search-button_focus-overlay"></span>
            </button>
            <button data-test-label="sp-pdf-viewer-search-close-button" data-id="closeButton" class="sp-pdf-search-button sp-pdf-search-button--no-border-radius sp-pdf-search-button--icon-16 close-button">
                <span class="sp-pdf-search-button_wrapper">
                    <span class="icon">
                    ${PDF_VIEWER_SVG_ICONS[PdfViewerIconType.CloseFilled]}
                    </span>
                </span>
                <span class="sp-pdf-search-button_focus-overlay"></span>
            </button>
        </div>`;
        this.searchElement = this.container.querySelector(`[data-id='search']`);
        this.input = this.container.querySelector('input');
        this.loadingElement = this.container.querySelector(`[data-id='loading']`);
        this.noResultsElement = this.container.querySelector(`[data-id='noResults']`);
        this.searchButtonsPrevElement = this.container.querySelector(`[data-id='prevButton']`);
        this.searchButtonsNextElement = this.container.querySelector(`[data-id='nextButton']`);
        this.closeButtonElement = this.container.querySelector(`[data-id='closeButton']`);
        this.addEventListeners();
    }
    addEventListeners() {
        this.eventBus.on(PdfViewerEventType.FocusChange, this.onPdfViewerFocusChangeEvent);
        this.eventBus.on(PdfViewerEventType.InitializeSearch, this.onPdfViewerInitializeSearchEvent);
        this.eventBus.on(PdfViewerEventType.FetchSearchResults, this.onPdfViewerFetchSearchResultsEvent);
        this.searchButtonsPrevElement.addEventListener('click', this.onGoToPrevPageClick);
        this.searchButtonsNextElement.addEventListener('click', this.onGoToNextPageClick);
        this.closeButtonElement.addEventListener('click', this.onPdfViewerHideSearch);
        this.initializeSearchDebounced = debounce(() => {
            this.initializeSearch(this.input.value);
        }, 500);
        this.input.addEventListener('keydown', this.onInputKeydownEvent);
        this.input.addEventListener('input', this.onInputInputEvent);
        this.input.addEventListener('focusin', this.onInputFocusinEvent);
        this.input.addEventListener('focusout', this.onInputFocusoutEvent);
    }
    removeEventListeners() {
        var _a, _b, _c, _d, _e, _f, _g, _h;
        this.eventBus.off(PdfViewerEventType.FocusChange, this.onPdfViewerFocusChangeEvent);
        this.eventBus.off(PdfViewerEventType.InitializeSearch, this.onPdfViewerInitializeSearchEvent);
        this.eventBus.off(PdfViewerEventType.FetchSearchResults, this.onPdfViewerFetchSearchResultsEvent);
        this.eventBus.off(PdfViewerEventType.SearchIsAvailable, this.onPdfViewerSearchIsAvailableEvent);
        this.eventBus.off(PdfViewerEventType.ToggleSearch, this.onPdfViewerToggleSearch);
        (_a = this.searchButtonsPrevElement) === null || _a === void 0 ? void 0 : _a.removeEventListener('click', this.onGoToPrevPageClick);
        (_b = this.searchButtonsNextElement) === null || _b === void 0 ? void 0 : _b.addEventListener('click', this.onGoToNextPageClick);
        (_c = this.closeButtonElement) === null || _c === void 0 ? void 0 : _c.removeEventListener('click', this.onPdfViewerHideSearch);
        (_d = this.input) === null || _d === void 0 ? void 0 : _d.removeEventListener('keydown', this.onInputKeydownEvent);
        (_e = this.input) === null || _e === void 0 ? void 0 : _e.addEventListener('input', this.onInputInputEvent);
        (_f = this.input) === null || _f === void 0 ? void 0 : _f.addEventListener('focusin', this.onInputFocusinEvent);
        (_g = this.input) === null || _g === void 0 ? void 0 : _g.addEventListener('focusout', this.onInputFocusoutEvent);
        (_h = this.initializeSearchDebounced) === null || _h === void 0 ? void 0 : _h.cancel();
    }
    showSearch() {
        this.render();
        this.setSearchState(this.searchState);
        this.input.focus();
    }
    isDisplayed() {
        return this.container.textContent !== '';
    }
    setSearchState(searchState) {
        this.noResultsElement.classList.add('hidden');
        this.loadingElement.classList.add('hidden');
        this.searchButtonsPrevElement.classList.add('hidden');
        this.searchButtonsNextElement.classList.add('hidden');
        if (searchState.text === '') {
            return;
        }
        if (searchState.status === SearchStatus.Pending) {
            this.loadingElement.classList.remove('hidden');
            return;
        }
        if (searchState.text !== '' && searchState.pages.length === 0) {
            this.noResultsElement.classList.remove('hidden');
        }
        if (searchState.pages.length > 1) {
            this.searchButtonsPrevElement.classList.remove('hidden');
            this.searchButtonsNextElement.classList.remove('hidden');
        }
        // move cursor if needed
        this.input.blur();
        const end = this.input.selectionEnd;
        this.input.setSelectionRange(end, end);
        this.input.focus();
    }
    destroy() {
        this.removeEventListeners();
        this.container.innerHTML = '';
        window.removeEventListener('keydown', this.onWindowKeydownEvent);
    }
}
//# sourceMappingURL=pdf-search.component.js.map